import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Context Providers
import { FooterProvider } from './contexts/FooterContext';

// Components - keep above-the-fold components in the main bundle
import { ScrollToTop } from './components';

// Eagerly load Home (the most-visited page); split everything else.
import Home from './pages/Home';

// Lazy-load secondary pages so the homepage bundle stays small.
const CountryPage = lazy(() => import('./pages/CountryPage'));
const WineTypePage = lazy(() => import('./pages/WineTypePage'));
const RegionPage = lazy(() => import('./pages/RegionPage'));
const AppellationPage = lazy(() => import('./pages/AppellationPage'));
const VarietyPage = lazy(() => import('./pages/VarietyPage'));
const MultiFilterPage = lazy(() => import('./pages/MultiFilterPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));

const AdminPage = lazy(() => import('./pages/AdminPage'));

// Lazy-load the chatbot — it's a floating bubble below the fold and not
// needed on first paint. Defer mount until the browser is idle.
const WineChatBot = lazy(() => import('./components/WineChatBot'));

const DeferredChatBot = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const idle =
      typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 2000);
    const cancel =
      typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function'
        ? window.cancelIdleCallback
        : clearTimeout;
    const handle = idle(() => setReady(true));
    return () => cancel(handle);
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <WineChatBot />
    </Suspense>
  );
};

function App() {
  return (
    <FooterProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Suspense fallback={null}>
            <Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />

              {/* Admin (must be before the catch-all wine detail route below) */}
              <Route path="/admin" element={<AdminPage />} />

              {/* Single filter pages */}
              <Route path="/country/:country" element={<CountryPage />} />
              <Route path="/type/:type" element={<WineTypePage />} />
              <Route path="/region/:region" element={<RegionPage />} />
              <Route path="/appellation/:appellation" element={<AppellationPage />} />
              <Route path="/variety/:variety" element={<VarietyPage />} />

              {/* Multi-filter combination pages */}
              <Route path="/type/:type/country/:country" element={<MultiFilterPage />} />
              <Route path="/type/:type/region/:region" element={<MultiFilterPage />} />
              <Route path="/type/:type/variety/:variety" element={<MultiFilterPage />} />
              <Route path="/variety/:variety/country/:country" element={<MultiFilterPage />} />
              <Route path="/variety/:variety/region/:region" element={<MultiFilterPage />} />
              <Route path="/country/:country/variety/:variety" element={<MultiFilterPage />} />
              <Route path="/country/:country/region/:region" element={<MultiFilterPage />} />
              <Route path="/country/:country/type/:type" element={<MultiFilterPage />} />
              <Route path="/region/:region/variety/:variety" element={<MultiFilterPage />} />
              <Route path="/region/:region/type/:type" element={<MultiFilterPage />} />

              {/* Product detail page - catch all other paths as wine detail */}
              <Route path="/:dealPath" element={<ProductDetailPage />} />
              <Route path="/*" element={<ProductDetailPage />} />
            </Routes>
          </Suspense>

          {/* Global Components */}
          <DeferredChatBot />
          <Toaster position="top-right" />
        </div>
      </Router>
    </FooterProvider>
  );
}

export default App;
